import {
  PrePostNight,
  Extension,
  Itinerary,
  ItineraryHeader,
  Upgrade,
  RoomDetail,
  Excursion,
  ExtensionStyle,
  Day,
  RoomUpgrades,
  ChoiceOnTour,
  ShareEmail,
  ShareEmailForm
} from "@/types/dto/pdp/pdp-itinerary";
import processTourDates from "../utils/process-tour-dates";
import basicInfoFactory from "../common/basic-info-factory";

const getImageAltTag = (image: any): string => {
  if (Array.isArray(image)) {
    return getImageAltTag(image[0]);
  }

  return (
    image?.imageAltTag ??
    image?.altTag ??
    image?.imageAlt ??
    image?.webAltTag ??
    ""
  );
};

export const roomDetailFactory = (data: any = {}): RoomDetail => ({
  description: data?.description ?? "",
  room: data?.room ?? 0,
  nights: data?.nights ?? 0,
  price: data?.price ?? ""
});

export const excursionFactory = (data: any = {}): Excursion => ({
  title: data?.title ?? "",
  imageUrl: data?.imgurl ?? undefined,
  price: data?.price ?? undefined,
  time: data?.hours ?? "",
  passengers: data?.minimumPassengers ?? "",
  transportation: data?.transportationIncluded ?? false,
  fullDescription: data?.description ?? ""
});

export const choiceFactory = (data: any = {}): ChoiceOnTour => ({
  title: data?.title ?? "",
  imageUrl: data?.imageUrl ?? undefined
});

export const roomUpgradesFactory = (data: any = {}): RoomUpgrades => ({
  description: data?.description ?? "",
  title: data?.title ?? "",
  location: data?.location ?? "",
  imageUrl: data?.imageUrl ?? "",
  rooms: data?.records?.map((room: any) => roomDetailFactory(room)) ?? []
});

export const upgradeFactory = (data: any = {}): Upgrade => ({
  choiceOnTour: data?.choiceOnTours?.map((choice: any) => choiceFactory(choice)) ?? [],
  roomUpgrades: data?.roomCruiseUpgrades?.map((room: any) => roomUpgradesFactory(room)) ?? [],
  excursion: data?.excursions?.map((excursion: any) => excursionFactory(excursion)) ?? []
});

export const extensionStyleFactory = (data: any = {}): ExtensionStyle => ({
  name: data?.name ?? "Not Available",
  description: data?.description ?? "",
  imageUrl: data?.imageUrl ?? "",
  imageAltTag: data?.imageAltTag ?? "Extension Style"
});

export const itineraryHeaderFactory = (data: any = {}): ItineraryHeader => ({
  days: data?.days ?? 0,
  nights: data?.nights ?? 0,
  meals: data?.meals ?? 0,
  selectedDate: data?.tourStartDate && data?.tourEndDate ? processTourDates(data.tourStartDate, data.tourEndDate) : "",
  bestTimeToTravel: data?.bestTimeToTravel ?? "",
  mapImageUrl: data?.mapImageUrl ?? "",
  mapImageAltTag: data?.mapImageAltTag ?? data?.mapImageAlt ?? "",
  itineraryType: data?.itineraryType ?? ""
});

export const prePostNightFactory = (data: any = {}): PrePostNight => ({
  city: data?.location ?? "",
  location: data?.location ?? "",
  accommodation: data?.accommodation ?? "",
  activityLevelNumber: data?.activityLevelNumber ?? 0,
  activityLevelDescription: data?.activityLevelDescription ?? "",
  imageUrl: data?.imageUrl ?? "",
  imageAltTag: data?.imageAltTag ?? data?.imageAlt ?? "",
  price: data?.price ?? "",
  description: data?.description ?? ""
});

export const dayFactory = (data: any = {}): Day => {
  const result: Day = {
    dayNumber: data?.dayNumber ?? "1",
    dayHeading: data?.dayHeading ?? "",
    meals: data?.meals ?? "",
    accommodation: data?.dayAccommodation ?? "",
    imageUrl: data?.dayImage ?? "",
    imageAltTag:
      // getImageAltTag preserves explicit empty strings; || treats them as missing for fallback.
      getImageAltTag(data?.day_images_new) ||
      getImageAltTag(data?.dayImages) ||
      data?.dayAltText ||
      data?.imageAltTag ||
      data?.imageAlt ||
      "",
    dayVideo:
      data?.dayVideo ||
      data?.VideoId ||
      data?.videoId ||
      data?.wistiaVideoId ||
      data?.WistiaVideoId ||
      undefined,
    description: data?.dayDescription ?? "",
    upgrades: data?.packageUpgrades
      ? upgradeFactory(data.packageUpgrades)
      : upgradeFactory({})
  };

  // eslint-disable-next-line no-console
  console.log("dayFactory alt-tag input:", data);
  // eslint-disable-next-line no-console
  // console.log("dayFactory alt-tag output:", result.imageAltTag);

  return result;
};

export const extensionFactory = (data: any = {}): Extension => ({
  title: data?.title ?? undefined,
  price: data?.price ?? undefined,
  currency: data?.currency ?? undefined,
  description: data?.description ?? undefined,
  numberOfDays: data?.numberOfDays ?? undefined,
  imageUrl: data?.imageUrl ?? "",
  imageAltTag: data?.imageAltTag ?? data?.imageAlt ?? "",
  wistiaVideoId: data?.wistiaVideoId ?? undefined,
  extensionStyle: typeof data?.extensionStyle === "string"
    ? data.extensionStyle
    : data?.extensionStyle?.style ?? "",
  activityLevelNumber: data?.activityLevel ?? 0,
  activityLevelDescription: data?.activityLevelDescription ?? "",
  days: data?.itinerary?.map((day: any) => dayFactory(day)) ?? [],
  meals: data?.meals ?? ""
});

export const itineraryFactory = (data: any = {}): Itinerary => {
  // eslint-disable-next-line no-console
  console.log("itineraryFactory alt-tag input:", data);

  const result: Itinerary = {
    itineraryHeader: itineraryHeaderFactory(data),
    preNight: data?.preNight ? prePostNightFactory(data.preNight) : undefined,
    preTour: data?.preTour ? extensionFactory(data.preTour) : undefined,
    days: data?.dailyItinerary?.map((day: any) => dayFactory(day)) ?? [],
    postTour: data?.postTour ? extensionFactory(data.postTour) : undefined,
    postNight: data?.postNight ? prePostNightFactory(data.postNight) : undefined,
    excursions: data?.excursions?.map((excursion: any) => excursionFactory(excursion)) ?? []
  };

  // eslint-disable-next-line no-console
  console.log("itineraryFactory alt-tag output:", {
    days: result.days.map((day) => day.imageAltTag),
    preTourDays: result.preTour?.days.map((day) => day.imageAltTag),
    postTourDays: result.postTour?.days.map((day) => day.imageAltTag)
  });

  return result;
};


export const shareEmailFormFactory = (data: any = {}): ShareEmailForm => ({
  formId: data?.formId ?? "",
  formName: data?.formName ?? "",
  formTitle: data?.formTitle ?? "",
  portalId: data?.portalId ?? ""
});

export const shareEmailFactory = (data: any = {}): ShareEmail => ({
  basicInfo: basicInfoFactory(data?.basicInfo),
  shareEmailForm: shareEmailFormFactory(data?.shareEmailForm)
});